import io from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';
const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || 'https://multi-counter.bancheng.tech';

// WebSocket 連接（單例）
let socket = null;
let heartbeatInterval = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const HEARTBEAT_INTERVAL = 25000; // 25 秒發送一次心跳

/**
 * 啟動心跳機制
 */
const startHeartbeat = () => {
  // 清除舊的心跳
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  // 設置新的心跳
  heartbeatInterval = setInterval(() => {
    if (socket && socket.connected) {
      socket.emit('ping');
      console.log('💓 發送心跳');
    }
  }, HEARTBEAT_INTERVAL);
};

/**
 * 停止心跳機制
 */
const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

/**
 * 初始化 WebSocket 連接
 */
export const initializeSocket = () => {
  if (socket && socket.connected) {
    return socket;
  }

  socket = io(WEBSOCKET_URL, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
    // 添加心跳配置
    pingInterval: 25000,
    pingTimeout: 60000
  });

  socket.on('connect', () => {
    console.log('✅ WebSocket 已連接:', socket.id);
    reconnectAttempts = 0;
    startHeartbeat();
  });

  socket.on('disconnect', (reason) => {
    console.log('❌ WebSocket 已斷線:', reason);
    stopHeartbeat();

    // 如果是伺服器主動斷線，嘗試重連
    if (reason === 'io server disconnect') {
      socket.connect();
    }
  });

  socket.on('connect_error', (error) => {
    reconnectAttempts++;
    console.error(`WebSocket 連接錯誤 (嘗試 ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}):`, error.message);

    // 如果重連次數過多，警告使用者
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('⚠️ WebSocket 重連次數過多，請檢查網路連接');
    }
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log(`🔄 WebSocket 重連成功 (第 ${attemptNumber} 次嘗試)`);
    reconnectAttempts = 0;
  });

  socket.on('reconnect_attempt', (attemptNumber) => {
    console.log(`🔄 嘗試重連 WebSocket (第 ${attemptNumber} 次)`);
  });

  socket.on('reconnect_failed', () => {
    console.error('❌ WebSocket 重連失敗');
  });

  // 監聽伺服器的 pong 回應
  socket.on('pong', () => {
    console.log('💓 收到心跳回應');
  });

  return socket;
};

/**
 * 取得 WebSocket 實例
 */
export const getSocket = () => {
  if (!socket) {
    initializeSocket();
  }
  return socket;
};

/**
 * 訂閱單一計時器的即時更新
 * @param {string} timerId - 計時器 ID
 * @param {function} callback - 更新回調函式
 * @returns {function} - 取消訂閱函式
 */
export const subscribeToTimer = (timerId, callback) => {
  const socket = getSocket();
  let isSubscribed = true;

  // 訂閱函數
  const subscribe = () => {
    if (isSubscribed && socket.connected) {
      socket.emit('subscribe', { collection: 'timers', documentId: timerId });
      console.log(`📡 訂閱計時器更新: ${timerId}`);
    }
  };

  // 初始訂閱
  subscribe();

  // 初始載入資料
  getTimer(timerId).then(callback).catch(error => {
    console.error('❌ 初始載入計時器失敗:', error);
  });

  // 監聽計時器更新事件
  const updateHandler = (data) => {
    console.log('📨 收到計時器更新:', data);
    if (data.fullDocument) {
      // 轉換 _id 為 id (相容 Firebase 格式)
      if (data.fullDocument._id && !data.fullDocument.id) {
        data.fullDocument.id = data.fullDocument._id;
      }
      callback(data.fullDocument);
    }
  };

  // 重連後重新訂閱
  const reconnectHandler = () => {
    console.log('🔄 重連後重新訂閱計時器:', timerId);
    subscribe();
    // 重新載入資料
    getTimer(timerId).then(callback).catch(error => {
      console.error('❌ 重連後載入計時器失敗:', error);
    });
  };

  socket.on('timer:update', updateHandler);
  socket.on('connect', reconnectHandler);

  // 返回取消訂閱函式
  return () => {
    isSubscribed = false;
    socket.off('timer:update', updateHandler);
    socket.off('connect', reconnectHandler);
    if (socket.connected) {
      socket.emit('unsubscribe', { collection: 'timers', documentId: timerId });
    }
    console.log(`🔕 取消訂閱計時器: ${timerId}`);
  };
};

/**
 * 訂閱所有計時器的即時更新
 * @param {function} callback - 更新回調函式
 * @returns {function} - 取消訂閱函式
 */
export const subscribeToAllTimers = (callback) => {
  const socket = getSocket();
  let isSubscribed = true;

  // 訂閱函數
  const subscribe = () => {
    if (isSubscribed && socket.connected) {
      socket.emit('subscribe', { collection: 'timers' });
      console.log('📡 訂閱所有計時器更新');
    }
  };

  // 初始訂閱
  subscribe();

  // 初始載入資料
  getAllTimers().then(callback).catch(error => {
    console.error('❌ 初始載入計時器失敗:', error);
  });

  // 監聽計時器更新事件
  const updateHandler = (data) => {
    console.log('📨 收到計時器更新（全部）:', data);
    // 重新載入所有計時器
    getAllTimers().then(callback);
  };

  // 重連後重新訂閱
  const reconnectHandler = () => {
    console.log('🔄 重連後重新訂閱所有計時器');
    subscribe();
    // 重新載入資料
    getAllTimers().then(callback).catch(error => {
      console.error('❌ 重連後載入計時器失敗:', error);
    });
  };

  socket.on('timers:update', updateHandler);
  socket.on('connect', reconnectHandler);

  // 返回取消訂閱函式
  return () => {
    isSubscribed = false;
    socket.off('timers:update', updateHandler);
    socket.off('connect', reconnectHandler);
    if (socket.connected) {
      socket.emit('unsubscribe', { collection: 'timers' });
    }
    console.log('🔕 取消訂閱所有計時器');
  };
};

/**
 * 取得單一計時器
 */
export const getTimer = async (timerId) => {
  try {
    const response = await fetch(`${API_URL}/timers/${timerId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    // 轉換 _id 為 id (相容 Firebase 格式)
    if (data._id && !data.id) {
      data.id = data._id;
    }

    console.log('✅ 取得計時器:', timerId, data);
    return data;
  } catch (error) {
    console.error('❌ 取得計時器錯誤:', error);
    throw error;
  }
};

/**
 * 取得所有計時器
 */
export const getAllTimers = async () => {
  try {
    const response = await fetch(`${API_URL}/timers`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    let data = await response.json();

    // 轉換 _id 為 id (相容 Firebase 格式)
    if (Array.isArray(data)) {
      data = data.map(timer => {
        if (timer._id && !timer.id) {
          timer.id = timer._id;
        }
        return timer;
      });
    }

    console.log('✅ 取得所有計時器:', data.length, '筆');
    return data;
  } catch (error) {
    console.error('❌ 取得所有計時器錯誤:', error);
    throw error;
  }
};

/**
 * 更新計時器
 */
export const updateTimer = async (timerId, updates) => {
  try {
    const response = await fetch(`${API_URL}/timers/${timerId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('更新計時器錯誤:', error);
    throw error;
  }
};

/**
 * 刪除計時器
 */
export const deleteTimer = async (timerId) => {
  try {
    const response = await fetch(`${API_URL}/timers/${timerId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('刪除計時器錯誤:', error);
    throw error;
  }
};

/**
 * 取得計時器統計
 */
export const getTimerStats = async () => {
  try {
    const timers = await getAllTimers();
    return {
      total: timers.length,
      active: timers.filter(t => t.status === 'active').length,
      paused: timers.filter(t => t.status === 'paused').length,
      completed: timers.filter(t => t.status === 'completed').length,
    };
  } catch (error) {
    console.error('取得統計錯誤:', error);
    throw error;
  }
};

// Placeholder functions for Admin page - implement as needed
export const getAllUsers = async () => {
  // TODO: Implement VPS API endpoint for users
  return [];
};

export const getSyncLogs = async () => {
  // TODO: Implement VPS API endpoint for sync logs  
  return [];
};

export const createTimer = async (timerData) => {
  // TODO: Implement VPS API endpoint for creating timer
  console.warn('createTimer not implemented yet');
  return timerData;
};

export const getUserData = async (userId) => {
  // TODO: Implement VPS API endpoint for user data
  return null;
};
